import { createFileRoute } from '@tanstack/react-router';
import { Header } from '@/components/Header';
import { AddEventAttendees } from '@/pages/AddEventAttendees';
import { useEvent } from '@/lib/hooks/useEvents';
import { useEventAttendance } from '@/lib/hooks/useAttendance';
import { useEventRsvps } from '@/lib/hooks/useRsvps';
import { useAllUserProfiles, useUserProfile } from '@/lib/hooks/useUserProfile';

export const Route = createFileRoute(
  '/_authenticated/events/$eventId/attendance/add'
)({
  component: AddEventAttendeesPage,
});

function AddEventAttendeesPage() {
  const { eventId } = Route.useParams();
  const eventIdNum = parseInt(eventId, 10);
  const { isAdmin, isLoading: isLoadingProfile } = useUserProfile();
  const {
    data: event,
    isLoading: isLoadingEvent,
    error: eventError,
  } = useEvent(eventIdNum);
  const {
    data: attendance = [],
    isLoading: isLoadingAttendance,
    error: attendanceError,
  } = useEventAttendance(eventIdNum);
  const {
    data: rsvps = [],
    isLoading: isLoadingRsvps,
    error: rsvpsError,
  } = useEventRsvps(eventIdNum);
  const {
    data: profiles = [],
    isLoading: isLoadingProfiles,
    error: profilesError,
  } = useAllUserProfiles();

  const isLoading =
    isLoadingEvent ||
    isLoadingAttendance ||
    isLoadingRsvps ||
    isLoadingProfiles ||
    isLoadingProfile;

  if (isLoading) {
    return (
      <>
        <Header enableBack additionalClasses="bg-slate-600" />
        <div className="flex justify-center items-center py-12">
          <p className="text-body text-foreground/60">Loading attendees...</p>
        </div>
      </>
    );
  }

  if (!isAdmin()) {
    return (
      <>
        <Header enableBack additionalClasses="bg-slate-600" />
        <div className="flex justify-center items-center py-12 px-4 text-center">
          <p className="text-body text-foreground/80">
            You do not have permission to update attendance for this event.
          </p>
        </div>
      </>
    );
  }

  if (eventError || attendanceError || rsvpsError || profilesError || !event) {
    return (
      <>
        <Header enableBack additionalClasses="bg-slate-600" />
        <div className="flex justify-center items-center py-12 px-4 text-center">
          <p className="text-body text-foreground/80">
            {eventError
              ? 'Failed to load event.'
              : attendanceError
              ? 'Failed to load attendance.'
              : rsvpsError
              ? 'Failed to load RSVPs.'
              : profilesError
              ? 'Failed to load user list.'
              : 'Event not found.'}
          </p>
        </div>
      </>
    );
  }

  // Filter RSVPs to only include those marked as "going"
  const goingRsvps = rsvps.filter((rsvp) => rsvp.status === 'yes');

  return (
    <div className="flex flex-col">
      <Header enableBack additionalClasses="bg-slate-600" />
      <AddEventAttendees
        event={event}
        attendance={attendance}
        profiles={profiles}
        rsvps={goingRsvps}
      />
    </div>
  );
}

